// TODO: delete this region config and all related tests, we don't give support to this region website

// NOTE: please keep this JSON config sorted alphabetically to keep consistency between region config files

import {
  PaymentMethod,
  SUPPORTED_COUNTRY,
  SUPPORTED_LANGUAGE,
  MaxAgeExpiration,
} from 'utils/constants';
import { OrderSearchOperationName } from 'ui/forms/OrderLookUp/types';
import { commonConfig } from 'sites/common/config';

import { SiteConfig } from '../types';

import { auStates } from './states';
import { auPostalCodeValidation } from './validations';
import { AUFieldProps } from './fields';
import { AUForms } from './forms';

export const config: SiteConfig = {
  ...commonConfig,
  addressCountries: [{ label: 'Australia', value: SUPPORTED_COUNTRY.AU }],
  addressFormat: [
    ['firstName', 'lastName'],
    ['address1'],
    ['address2'],
    ['city'],
    ['stateCode', 'postalCode'],
    ['countryCode'],
  ],
  addressFormatOneLiner:
    '@{firstName} @{lastName} - @{address1} @{address2} @{city}, @{postalCode} @{stateCode} @{countryCode}',
  addressSearch: { billing: ['AU'], shipping: ['AU'] },
  addressStates: { AU: auStates },
  analytics: { releaseVersion: `Australia | ${process.env.VERSION}` },
  applePaySupportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
  badgeIdsExcludedBySaleStatus: [],
  bloomreach: { objectName: 'br_data', viewId: 'au' },
  charactersNeededToSubmitSearch: 1,
  countryCode: 'au',
  countryName: 'Australia',
  currency: { code: 'AUD', decimalsToDisplay: 2, symbol: '$' },
  defaultPaymentMethod: PaymentMethod.SCHEME,
  fieldProps: { ...AUFieldProps },
  flag: '🇦🇺',
  forms: { ...AUForms },
  legal: {
    privacyPolicy:
      'https://au.puma.com/au/en/privacy-policy/PRIVACY_POLICY.html',
    termsAndConditions:
      'https://au.puma.com/au/en/terms-of-purchase/TERMS_OF_PURCHASE.html',
  },
  liveChat: {
    provider: 'zendesk',
    script:
      'https://static.zdassets.com/ekr/snippet.js?key=ce75f8f8-3d45-45dc-883d-39d7b9044ec1',
  },
  maxAgeCookiesExpires: MaxAgeExpiration.DAILY,
  maxCountRecordKeeper: 0,
  maximumApplicableCards: 1,
  myAccountDashboardConfiguration: [
    {
      childOrder: [
        'profileComponent',
        'passwordComponent',
        'preferencesComponent',
        'ambassadorComponent',
        'storeCreditsComponent',
      ],
      name: 'leftColumn',
    },
    {
      childOrder: ['addressBookComponent', 'orderHistoryComponent'],
      name: 'rightColumn',
    },
  ],
  newsletter: {
    contactSources: { checkOut: '5', footer: '1', signUp: '6' },
    fields: {
      gender: { required: true, values: ['male', 'female', 'noAnswer'] },
    },
  },
  orderLookUpQuery: OrderSearchOperationName['SearchOrderByEmailPostcode'],
  orderedPaymentPartners: [
    'visa',
    'master',
    'maestro',
    'payPal',
    'klarna',
    'zip',
    'applePay',
    'googlePay',
    'weChatPay',
    'alipay',
  ],
  paymentMethodsMixedWithGiftCard: [
    PaymentMethod.SCHEME,
    PaymentMethod.ADYEN_PAYPAL,
    PaymentMethod.AFTERPAY_TOUCH,
    PaymentMethod.ZIP_PAY,
    PaymentMethod.KLARNA,
    PaymentMethod.KLARNA_ACCOUNT,
    PaymentMethod.ALIPAY,
    PaymentMethod.WECHAT_PAY,
  ],
  pdpVideosConfig: {
    ...commonConfig.pdpVideosConfig,
    numberOfLoops: 2,
    posterSecond: 4,
  },
  priceInputMaxLength: 4,
  priceSliderInterval: 10,
  region: 'oceania',
  regionCode: 'AUS',
  savedCreditCardTemplate: ['new', 'saved'],
  searchEngineIndexableLanguages: [SUPPORTED_LANGUAGE.EN],
  searchSymbolsMinValue: 2,
  selectablePaymentMethods: [
    PaymentMethod.AMBASSADOR_CARD,
    PaymentMethod.SCHEME,
    PaymentMethod.ADYEN_PAYPAL,
    PaymentMethod.AFTERPAY_TOUCH,
    PaymentMethod.ZIP_PAY,
    PaymentMethod.KLARNA,
    PaymentMethod.KLARNA_ACCOUNT,
    PaymentMethod.ALIPAY,
    PaymentMethod.WECHAT_PAY,
  ],
  socialMediaLinks: {
    facebook: 'https://www.facebook.com/pumaaustralia',
    instagram: 'https://instagram.com/pumaau',
    pinterest: 'https://www.pinterest.com.au/puma/',
  },
  staticFeatures: {
    ...commonConfig.staticFeatures,
    addAddressResultErrorDisabled: true,
    allowGuestAccessToOrderDetails: true,
    authorityToLeaveEnabled: true,
    canRemoveWishlistItemsFromPDP: true,
    displayPromoDiscountBreakdown: false,
    enableModelMeasurements: true,
    parcelLabTrackingEnabled: true,
    personalizationEnabled: true,
    popoverLoginEnabled: true,
    productFilterCollapsible: false,
    returnsOnOrderDetailsPage: true,
    showAddAddressButtonInAccountDashboard: false,
    showCookieBanner: true,
    showCookieConsentLayer: true,
    showFinancialIncentivesLink: false,
    showHeaderSubHeaderProductDetail: false,
    showModifiedResetPassFormPopup: false,
    showNewsletterSignupOnOrderConfirmation: false,
    showOrderSummaryAtOrderHistory: true,
    showOrderTrackingNumberInOrderDetails: true,
    showPaymentPartnersInFooter: true,
    showPhoneInCheckout: false,
    showPinterestFooterIcon: true,
    showPriceDifference: true,
    showPromoExclusionMsgAboveProductDetails: true,
    showPromoExclusionMsgInBaseSize: true,
    showSaleBadgeOnProductImageEnabled: true,
    showTimeZoneName: true,
    singInInConfirmationForm: true,
    useNewPaymentForm: true,
  },
  supportedLocales: ['en-au'],
  validation: {
    address: {
      line1: '^[a-zA-Z0-9+.,()/\\"\'\\- ]+$',
      line2: '^[a-zA-Z0-9+.,()/\\"\'\\- ]+$',
    },
    email: '^[\\w.%+-]+@[\\w.-]+\\.[\\w]{2,6}$',
    name: {
      length: {
        max: 15,
        min: commonConfig.validation.name.length.min,
      },
      regex: "^[a-zA-Z'\\- ]*$",
    },
    orderNo: '^[0-9]{9}$',
    password:
      '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[_!@#$%^&*.+(){}~?])(?=.{8,})',
    phone: {
      length: {
        max: 15,
        min: commonConfig.validation.phone.length.min,
      },
      prefix: '+61',
      regex:
        '^\\+61\\s?(?:[123578]\\s?\\d{4}\\s?\\d{4}|4(?:\\d{2}\\s?\\d{3}\\s?\\d{3}|\\s?\\d{4}\\s?\\d{4}))$',
    },
    postalCode: { AU: auPostalCodeValidation },
  },
};
